import * as React from "react"
import { cn } from "@/lib/utils"

type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * 클릭 가능한 카드에서만 사용:
   * <Card data-interactive> ... </Card>
   */
  "data-interactive"?: boolean
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Base
        "rounded-2xl border border-gray-200/70 bg-white/95 text-gray-900 shadow-sm",
        // Motion
        "transition-[transform,box-shadow,border-color,background-color] duration-200",
        // Optional interactive style
        props["data-interactive"] &&
          "cursor-pointer hover:-translate-y-[1px] hover:shadow-md hover:border-gray-300/80 active:translate-y-0 active:shadow-sm",
        className
      )}
      {...props}
    />
  )
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Mobile-friendly padding
        "flex flex-col gap-1.5 p-4 sm:p-6",
        className
      )}
      {...props}
    />
  )
)
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        // 기존 2xl은 카드 타이틀로 큰 편 → 기본은 18px~20px
        "text-lg sm:text-xl font-semibold leading-tight tracking-tight",
        className
      )}
      {...props}
    />
  )
)
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn(
        "text-sm text-gray-600 leading-relaxed",
        className
      )}
      {...props}
    />
  )
)
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Header 아래 컨텐츠는 위쪽 패딩이 과해지기 쉬움
        "px-4 pb-4 sm:px-6 sm:pb-6",
        className
      )}
      {...props}
    />
  )
)
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Footer는 버튼/링크가 많아서 모바일에서 답답 → padding 축소
        "flex items-center px-4 pb-4 sm:px-6 sm:pb-6",
        className
      )}
      {...props}
    />
  )
)
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
